import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { RefreshSievesAdditionButton } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/Components/SieveAdditionFieldSet';
import { SievingRecord } from 'Components/Worksheets/PsdSieveAnalysisWorksheet/types';
import { ISieveSize } from 'Data/ViewModels/Equipment/ISieveSize';
import { hasNonEmptyValue, hasValue, valueOrDefault } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import { descend, range, sort } from 'ramda';
import React, { useEffect, useState } from 'react';
import { MdRemoveCircle } from 'react-icons/md';
import { useDispatch, useSelector } from 'react-redux';
import { actions } from '../../../../../../../Data/ReduxViewModels/WorksheetEditor';
import deepEquals from '../../../../../../../Lib/Object/deepEquals';
import { Button, ConfirmDialog, Field, IMessageProps, Label, NumericInput, TextOutput } from '../../../../../../Core';
import { JoinedFormattedText } from '../../../../../../Core/FormattedText';
import { withInjectedProps } from '../../../../../../Core/InjectedPropsContext';
import { Grid, GridFieldSection } from '../../../../../../Layout';
import { LimitTextField, WorksheetEquipmentDropdown } from '../../../../../Core';
import { ManageSeparationsButton, PortionSievingAddSieveButton } from '../../../../Components';

let PortionSievingRecordGridSection: React.FC<IWorksheetComponent> = ({
  namePrefix,
  disabled,
  createWorksheetSelector,
}) => {
  const dispatch = useDispatch();

  // Ensure a sieving structure exists (so that defaults are applied, etc)
  // TODO: Move to backend?
  const doesSievingExist = useSelector(pipe(createWorksheetSelector!(''), hasNonEmptyValue));
  useEffect(() => {
    if (disabled) return;
    if (!doesSievingExist) {
      dispatch(actions.setValue({ name: `${namePrefix}`, value: {}, sync: true }));
    }
  }, [dispatch, namePrefix, doesSievingExist, disabled]);

  const isManageSeparationsDisabled = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.Method', false), (m) => m === 'B')
  );

  const areSieveMassesCumulative = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.AreSieveMassesCumulative', false), valueOrDefault(false))
  );
  const arePanMassesCumulative = useSelector(
    pipe(createWorksheetSelector!('data.Preparation.ArePanMassesCumulative', false), valueOrDefault(false))
  );

  const defaultMassReadingColumnCount = useSelector(
    pipe(createWorksheetSelector!('Records'), (r) =>
      r && r.length > 0 ? Math.max(...r?.map((r: SievingRecord) => r.MassRetainedReadings?.length ?? 1), 1) : 1
    )
  );

  const [massReadingColumnCount, setMassReadingColumnCount] = useState(defaultMassReadingColumnCount);

  const isDataInLastMassReadingColumn: boolean = useSelector(
    pipe(
      createWorksheetSelector!('Records'),
      (r) => r && r.some((r: SievingRecord) => hasValue(r.MassRetainedReadings?.[massReadingColumnCount - 1]))
    )
  );

  type RecordRowDefinition = {
    SieveSize?: ISieveSize;
    IsRequired?: boolean;
    index: number;
  };
  const recordRows: RecordRowDefinition[] = useSelector(
    pipe(createWorksheetSelector!('Records'), valueOrDefault([]), (records: SievingRecord[]) => {
      if (records.some((r) => !r.SieveSize?.Size || r.SieveSize.Size.uom !== records[0].SieveSize?.Size?.uom))
        throw new Error(
          'Cannot compare sieves sizes for sieving records. Sizes are missing or are defined with inconsistent units.'
        );
      return sort(
        descend((r) => Number(r.SieveSize?.Size?.value)),
        records.map((record, index) => ({
          SieveSize: record.SieveSize,
          IsRequired: record.IsRequired,
          index,
        }))
      );
    }),
    deepEquals
  );

  const recordLimitMessages = useSelector(
    pipe(createWorksheetSelector!('Records'), (records: SievingRecord[] | undefined | null): IMessageProps[] =>
      records?.some((r) => r.Limits?.some((l) => !l.IsPassing))
        ? [{ message: 'Results are outside of prescribed limits.', severity: 'error' }]
        : []
    ),
    deepEquals
  );
  const incrementMassReadingColumnCount = () => setMassReadingColumnCount(massReadingColumnCount + 1);

  const removeLastMassReadingColumn = () => {
    if (massReadingColumnCount > 1) {
      setMassReadingColumnCount(massReadingColumnCount - 1);
      dispatch(
        actions.patchValues({
          name: namePrefix!,
          keyValuePairs: range(0, recordRows.length).map((i) => ({
            key: `Records[${i}].MassRetainedReadings[${massReadingColumnCount - 1}]`,
            value: undefined,
          })),
          sync: true,
        })
      );
    }
  };

  type ColumnDefinition = {
    header: string;
    width: number;
    getFieldForRecord: (rrd: RecordRowDefinition) => React.ReactElement;
    panLabel?: React.ReactElement;
    panField?: React.ReactElement;
    headerButton?: React.ReactElement;
    hidden?: boolean;
    key?: string;
  };

  const gridColumns: ColumnDefinition[] = [
    {
      header: 'Sieve ID',
      width: 180,
      getFieldForRecord: (r: RecordRowDefinition) => (
        <Field
          input={<WorksheetEquipmentDropdown />}
          name={`Records[${r.index}].Sieve`}
          label="Sieve ID"
          equipmentType="sieve"
          queryParams={{ SieveSize: `${r.SieveSize!.Size!.value} ${r.SieveSize!.Size!.uom}` }}
        />
      ),
      key: 'sieveId',
    },
    {
      // First mass reading column (always shown)
      header: `${areSieveMassesCumulative ? 'Cumulative' : 'Individual'} Mass Retained (g)`,
      width: 120,
      getFieldForRecord: (r: RecordRowDefinition) => (
        <Field
          input={<NumericInput />}
          name={`Records[${r.index}].MassRetainedReadings[0]`}
          label={`${areSieveMassesCumulative ? 'Cumulative' : 'Individual'} Mass Retained`}
        />
      ),
      panLabel: (
        <Label className="Grid-Cell Grid-Cell-colHeader">
          {arePanMassesCumulative ? 'Cumulative' : 'Individual'} Mass Retained (g)
        </Label>
      ),
      panField: (
        <Field
          input={<NumericInput />}
          name={`PanMassRetainedReading`}
          label={`${arePanMassesCumulative ? 'Cumulative' : 'Individual'} Mass Retained`}
        />
      ),
      key: 'massReading0',
    },
    ...range(1, massReadingColumnCount).map((i) => ({
      // Additional mass reading columns
      header: '',
      width: 120,
      getFieldForRecord: (r: RecordRowDefinition) => (
        <Field input={<NumericInput />} name={`Records[${r.index}].MassRetainedReadings[${i}]`} />
      ),
      // Show button to remove last column
      headerButton:
        i === massReadingColumnCount - 1 ? (
          <ConfirmDialog
            confirm={isDataInLastMassReadingColumn}
            confirmText="Are you sure you want to remove this column of mass readings? Data cannot be recovered."
          >
            <Button subtle onClick={removeLastMassReadingColumn}>
              <MdRemoveCircle size="2em" />
            </Button>
          </ConfirmDialog>
        ) : undefined,
      key: `massReading${i}`,
    })),
    {
      // Sum of individual mass readings
      header: '',
      width: 80,
      getFieldForRecord: (r: RecordRowDefinition) => (
        <Field input={<TextOutput />} name={`Records[${r.index}].IndividualMassRetained`} />
      ),
      hidden: areSieveMassesCumulative || massReadingColumnCount <= 1,
      key: `massReadingSum`,
    },
    {
      // Calculated mass retained
      header: `${areSieveMassesCumulative ? 'Individual' : 'Cumulative'} Mass Retained (g)`,
      width: 120,
      getFieldForRecord: (r: RecordRowDefinition) => {
        // If entered as cumulative we need to show all individual reading masses here.
        // If entered as individual we just need to show the final cumulative mass.
        return areSieveMassesCumulative ? (
          <Field
            input={<TextOutput />}
            label={`Individual Mass Retained (g)`}
            value={
              <JoinedFormattedText
                blankReplacement="-"
                names={range(0, massReadingColumnCount).map(
                  (i) => `Records[${r.index}].IndividualMassRetainedReadings[${i}]`
                )}
              />
            }
          />
        ) : (
          <Field
            input={<TextOutput />}
            name={`Records[${r.index}].CumulativeMassRetained`}
            label={`Cumulative Mass Retained (g)`}
          />
        );
      },
      panLabel: (
        <Label className="Grid-Cell Grid-Cell-colHeader">
          {arePanMassesCumulative ? 'Individual' : 'Cumulative'} Mass Retained (g)
        </Label>
      ),
      panField: (
        <Field
          input={<TextOutput />}
          name={`${arePanMassesCumulative ? 'Individual' : 'Cumulative'}PanMassRetained`}
          label={`${arePanMassesCumulative ? 'Individual' : 'Cumulative'} Mass Retained`}
        />
      ),
      key: `calculatedMassRetained`,
    },
    {
      header: 'Individual Maximum Mass (g)',
      width: 140,
      getFieldForRecord: (r: RecordRowDefinition) => (
        <Field
          input={<TextOutput />}
          name={`Records[${r.index}].MaximumIndividualMassRetained`}
          label={'Individual Maximum Mass'}
        />
      ),
      key: `maximumMass`,
    },
    {
      header: 'Percent Passing (%)',
      width: 120,
      getFieldForRecord: (r: RecordRowDefinition) => (
        <Field input={<TextOutput />} label="Percent Passing (%)" name={`Records[${r.index}].PercentagePassing`} />
      ),
      key: `percentPassing`,
    },
    {
      header: 'Limit (%)',
      width: 120,
      getFieldForRecord: (r: RecordRowDefinition) => (
        <LimitTextField path={`Records[${r.index}].PercentagePassing`} label="Limit (%)" />
      ),
      key: `limit`,
    },
  ].filter((c: ColumnDefinition) => !c.hidden);

  type RowDefinition = {
    header: string;
    data: (React.ReactElement<any> | undefined)[];
    preventRemove: boolean;
    hidden?: boolean;
    onRemove?: () => any;
    key: string;
  };

  const gridRows: RowDefinition[] = [
    {
      // Header button
      header: '',
      data: gridColumns.map((c) => c.headerButton),
      preventRemove: true,
      hidden: !gridColumns.some((c) => !!c.headerButton),
      key: `headerButton`,
    },
    ...(recordRows?.map((r) => ({
      // Row for each sieving record
      header: r.SieveSize!.Caption,
      data: gridColumns.map((c) => c.getFieldForRecord(r)),
      preventRemove: r.IsRequired!,
      onRemove: () => dispatch(actions.removeArrayEntry({ name: `${namePrefix}.Records`, index: r.index, sync: true })),
      key: `sieveGridRow_${r.SieveSize!.Id}`,
    })) ?? []),
    {
      // Optional pan label row
      header: '',
      data: gridColumns.map((c) => c.panLabel),
      preventRemove: true,
      hidden: areSieveMassesCumulative === arePanMassesCumulative,
      key: 'panLabel',
    },
    {
      // Pan mass row
      header: 'Pan',
      data: gridColumns.map((c) => c.panField),
      preventRemove: true,
      key: 'panMass',
    },
  ].filter((r: RowDefinition) => !r.hidden);

  return (
    <>
      <div>
        <PortionSievingAddSieveButton />
        <Button disabled={recordRows.length === 0} onClick={incrementMassReadingColumnCount}>
          Sieve in Parts
        </Button>
        <ManageSeparationsButton maxSeparations={2} disabled={isManageSeparationsDisabled} />
        <RefreshSievesAdditionButton />
      </div>

      <GridFieldSection name="Records" messages={recordLimitMessages}>
        <Grid
          cornerHeader="Sieve Size"
          columns={gridColumns.map((c) => ({ key: c.key }))}
          colHeaders={gridColumns.map((c) => c.header)}
          colWidths={gridColumns.map((c) => c.width)}
          rowHeaders={gridRows.map((r) => r.header)}
          rowHeaderWidth={140}
          data={gridRows.map((r) => r.data)}
          onRemoveRow={(i: number) => gridRows[i].onRemove?.()}
          rows={gridRows.map((r) => ({ preventRemoveRow: r.preventRemove, key: r.key }))}
          removeConfirm={{
            confirm: true,
            confirmTitle: 'Sieving Record Deletion',
            confirmText: 'Are you sure you want to delete the selected sieving record?',
            confirmButtonText: 'Delete Sieving Record',
          }}
          tabDirection="vertical"
        />
      </GridFieldSection>
    </>
  );
};

PortionSievingRecordGridSection = withInjectedProps(PortionSievingRecordGridSection);

export { PortionSievingRecordGridSection };
export default PortionSievingRecordGridSection;
